import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { toast } from 'sonner';
import { templates as defaultTemplates } from '../templates';

const TemplatesContext = createContext();

export function TemplatesProvider({ children }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filter, setFilter] = useState('all'); // 'all', 'official', 'community', 'mine'

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            setLoading(true);

            // Get current user
            const { data: { user } } = await supabase.auth.getUser();

            // Fetch templates from DB (public ones + my own)
            let query = supabase
                .from('templates')
                .select('*')
                .order('downloads', { ascending: false });

            const { data: dbTemplates, error } = await query;

            if (error) throw error;

            // Combine with default hardcoded templates
            // We map default templates to match the DB schema shape roughly
            const formattedDefaultTemplates = defaultTemplates.map(t => ({
                ...t,
                is_official: true,
                is_public: true,
                created_by: 'system',
                downloads: 0, // Mock value
                tags: ['official']
            }));

            // Merge: Official + DB Templates
            // Note: DB templates might also contain "official" ones if we decided to migrate them later, 
            // but for now we treat the JS file as the source of truth for official ones.

            const allTemplates = [
                ...formattedDefaultTemplates,
                ...(dbTemplates || [])
            ];

            setTemplates(allTemplates);
        } catch (error) {
            console.error('Error fetching templates:', error);
            // Fallback to just default templates if DB fails
            const formattedDefaultTemplates = defaultTemplates.map(t => ({
                ...t,
                is_official: true,
                is_public: true,
                created_by: 'system',
                tags: ['official']
            }));
            setTemplates(formattedDefaultTemplates);
        } finally {
            setLoading(false);
        }
    };

    const publishTemplate = async (project, name, description, isPublic = true) => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Must be logged in to publish');

            const newTemplate = {
                created_by: user.id,
                name: name || project.name,
                description: description,
                screens: project.data, // The screens array
                is_public: isPublic,
                is_official: false,
                tags: ['community']
            };

            const { data, error } = await supabase
                .from('templates')
                .insert(newTemplate)
                .select()
                .single();

            if (error) throw error;

            setTemplates(prev => [...prev, data]);
            toast.success('Template published successfully!');
            return data;
        } catch (error) {
            console.error('Error publishing template:', error);
            toast.error('Failed to publish template');
            throw error;
        }
    };

    const deleteTemplate = async (templateId) => {
        try {
            const { error } = await supabase
                .from('templates')
                .delete()
                .eq('id', templateId);

            if (error) throw error;

            setTemplates(prev => prev.filter(t => t.id !== templateId));
            toast.success('Template deleted');
        } catch (error) {
            console.error('Error deleting template:', error);
            toast.error('Failed to delete template');
        }
    };

    // Filter logic
    const getFilteredTemplates = (userId) => {
        return templates.filter(t => {
            // Search filter
            const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase()));

            if (!matchesSearch) return false;

            // Category filter
            if (filter === 'all') return true;
            if (filter === 'official') return t.is_official;
            if (filter === 'community') return !t.is_official && t.is_public;
            if (filter === 'mine') return t.created_by === userId;

            return true;
        });
    };

    return (
        <TemplatesContext.Provider
            value={{
                templates,
                loading,
                searchQuery,
                setSearchQuery,
                filter,
                setFilter,
                fetchTemplates,
                publishTemplate,
                deleteTemplate,
                getFilteredTemplates
            }}
        >
            {children}
        </TemplatesContext.Provider>
    );
}

export const useTemplates = () => {
    const context = useContext(TemplatesContext);
    if (!context) {
        throw new Error('useTemplates must be used within TemplatesProvider');
    }
    return context;
};
